import { useMemo, useState } from 'react'
import { resolveOnSiteTheme } from '@marva/shared/theme-resolver'
import { chatPreviewDocument, type ChatPreviewSite } from '@marva/embed-script/src/preview/chat-preview'
import type { EmailBrandFacts } from '../../services/api'
import { Hint, PreviewFrame, SegmentedControl, Stack } from '../../ds'

/**
 * The Brand tab's Email / Chat switch (MAR-391) — one control, so Overview and How we look say
 * the same two words.
 */
export type PreviewSurface = 'email' | 'chat'
export const PreviewSurfaceControl = ({ value, onChange }: { value: PreviewSurface; onChange: (next: PreviewSurface) => void }) => (
  <SegmentedControl
    size="sm"
    label="Preview surface"
    value={value}
    options={[{ value: 'email', label: 'Email' }, { value: 'chat', label: 'Chat' }]}
    onChange={(next) => onChange(next as PreviewSurface)}
  />
)

interface ChatPreviewProps {
  /**
   * The facts the widget would read. `undefined` = nothing applies (no styling stored and none
   * drafted), so the widget keeps its own look — the same `null` theme the server sends.
   */
  facts: EmailBrandFacts | undefined
  assistantName?: string
}

/**
 * How the shopper's chat widget looks with these facts (MAR-391).
 *
 * Built from the widget's OWN pieces, never a lookalike: the facts go through
 * `resolveOnSiteTheme` — the resolver `generate-script` and `websocket-chat` hand the widget —
 * and the document is the widget package's `chatPreviewDocument`: its stylesheets, the theme
 * rule `applyMerchantTheme` writes, and the prompt card and launcher markup (pinned to the SDK
 * by the embed-script tests). D-42: the shopper surface is drawn inside a frame; only the frame
 * is the app's.
 *
 * ⚠️ What it is not: the words are a sample, and the page behind it is a plain light or dark
 * one. The widget picks its light or dark look from the storefront's background, which the
 * preview can only offer as a choice — so the copy says "a preview of the chat widget", never
 * "what shoppers see".
 *
 * Only colour and paper reach chat: the widget's font, corners and (lack of) logo are its own
 * (`resolveOnSiteTheme` projects `--m-font` and `--m-chip-radius` for the ask cards only), so
 * the hint says so rather than claiming all five facts.
 */
export default function ChatPreview({ facts, assistantName }: ChatPreviewProps) {
  const [site, setSite] = useState<ChatPreviewSite>('light')
  const factsKey = JSON.stringify(facts ?? null)
  // Resolved from the facts' VALUE: a parent re-render with an equal draft resolves nothing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const theme = useMemo(() => (facts ? resolveOnSiteTheme({ emailStyles: facts }) : null), [factsKey])
  const doc = useMemo(() => chatPreviewDocument({ theme, site, assistantName }), [theme, site, assistantName])

  // The colour chat actually paints, when the resolver deepened the merchant's for contrast.
  const painted = theme?.['--gold']
  // Only for a complete hex: a half-typed colour resolves to the default, which is not a deepening.
  const complete = !!facts && /^#[0-9a-f]{6}$/i.test(facts.brandColor)
  const deepened = complete && painted && painted.toUpperCase() !== facts!.brandColor.toUpperCase() ? painted.toUpperCase() : null

  return (
    <Stack gap="sm" data-testid="chat-preview">
      <SegmentedControl
        size="sm"
        label="Your site"
        value={site}
        options={[{ value: 'light', label: 'Light site' }, { value: 'dark', label: 'Dark site' }]}
        onChange={(next) => setSite(next as ChatPreviewSite)}
      />
      <PreviewFrame title="Chat widget preview" srcDoc={doc} sandbox="" height={330} compactHeight={330} />
      <Hint>
        {!facts
          ? 'No styling is set, so chat keeps Marva’s own look — light or dark to match your site.'
          // Defensive: `resolveOnSiteTheme` deepens any colour on the two papers past its contrast
          // floor, so no real facts reach this today. The resolver still owns that refusal (it
          // returns null, and the widget keeps its look), so the copy is here if it ever does.
          : !theme
            ? 'These colours fall below chat’s contrast minimum, so chat keeps Marva’s own look until they change.'
            // A half-typed colour resolves to Marva's gold: say so rather than claim it is hers.
            : !complete
              ? 'Chat takes your brand colour and paper once the colour is complete — until then it shows Marva’s gold. Typeface, corners and logo are for emails.'
              : `Chat takes your brand colour and paper${deepened ? ` (your colour deepened to ${deepened} so it stays readable)` : ''}. Typeface, corners and logo are for emails.`}
      </Hint>
      <Hint>A preview of the chat widget with these facts, drawn with the widget’s own styles. The words are a sample.</Hint>
    </Stack>
  )
}
